import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, eur } from "@/lib/api";
import { Button } from "@/components/ui/button";
import ReviewPrompt from "@/components/client/ReviewPrompt";
import { translations } from "@/lib/i18n";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PaymentReturn({ mode = "success" }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(mode === "cancel" ? "cancelled" : "checking");
  const [amount, setAmount] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const t = translations.FR;

  let ret = null;
  try { ret = JSON.parse(localStorage.getItem("novanext_pay_return") || "null"); } catch (e) { ret = null; }
  const backTo = ret ? `/restaurant/${ret.slug}/table/${ret.tableNumber}` : "/";
  const clientSessionId = localStorage.getItem("novanext_client_id");
  const theme = { primary: restaurant?.brand_primary || "#123026", gold: restaurant?.brand_secondary || "#C69C4A" };

  useEffect(() => {
    if (!ret?.slug) return;
    api.getRestaurant(ret.slug).then(setRestaurant).catch(() => {});
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (mode === "cancel" || !sessionId) return;
    let tries = 0;
    let active = true;
    const poll = async () => {
      try {
        const s = await api.paymentStatus(sessionId);
        setAmount(s.amount);
        if (s.payment_status === "paid") { setState("paid"); return; }
        if (s.payment_status === "refunded") { setState("refunded"); return; }
        if (s.payment_status === "failed" || s.status === "expired") { setState(s.status === "expired" ? "expired" : "failed"); return; }
      } catch (e) { /* keep polling */ }
      tries += 1;
      if (tries > 20) { setState("expired"); return; }
      if (active) setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [sessionId, mode]);

  const paid = state === "paid";
  const checking = state === "checking";
  const failed = state === "failed";
  const cancelled = state === "cancelled";
  const expired = state === "expired";
  const refunded = state === "refunded";
  const isError = failed || cancelled || expired;
  const errorTitle = cancelled ? "Paiement annulé" : expired ? "Paiement expiré" : "Paiement refusé";
  const errorHint = cancelled
    ? "Vous avez annulé le paiement. Vous pouvez réessayer depuis votre table."
    : expired
    ? "La session de paiement a expiré sans confirmation. Aucun montant n'a été débité. Réessayez depuis votre table."
    : "Le paiement n'a pas abouti (carte refusée). Aucun montant n'a été débité. Réessayez ou réglez auprès d'un serveur.";
  const isTest = (restaurant?.stripe_mode || "test") === "test";

  return (
    <div className="min-h-screen bg-[#FBF9F5] grid place-items-center px-6">
      <div data-testid="payment-return" className="w-full max-w-sm text-center animate-slide-up">
        {isTest && (
          <div data-testid="payment-test-banner" className="mb-6 rounded-xl bg-amber-100 border border-amber-300 text-amber-800 text-xs font-bold uppercase tracking-wider px-3 py-2">
            ⚠️ Mode TEST / Sandbox — paiement fictif
          </div>
        )}
        {checking && (
          <>
            <div className="w-20 h-20 rounded-full bg-[#EBF2EE] grid place-items-center mx-auto mb-5">
              <Loader2 className="w-9 h-9 text-[#123026] animate-spin" />
            </div>
            <h1 data-testid="payment-checking" className="font-serif-display text-2xl text-[#123026]">Vérification du paiement...</h1>
            <p className="text-sm text-[#8A918D] mt-2">Merci de patienter. La confirmation est validée côté serveur.</p>
          </>
        )}
        {paid && (
          <>
            <div className="w-20 h-20 rounded-full bg-[#EBF2EE] grid place-items-center mx-auto mb-5">
              <CheckCircle2 className="w-10 h-10 text-[#123026]" />
            </div>
            <h1 data-testid="payment-success" className="font-serif-display text-3xl text-[#123026]">Paiement confirmé !</h1>
            {amount != null && <p className="text-[#C69C4A] font-bold text-xl mt-1">{eur(amount)}</p>}
            <p className="text-sm text-[#8A918D] mt-2">Merci ! Votre table est réglée.</p>
            {ret?.slug && restaurant && restaurant.reviews_enabled !== false && (
              <div className="mt-8 text-left">
                <ReviewPrompt
                  slug={ret.slug}
                  tableSessionId={null}
                  clientSessionId={clientSessionId}
                  googleUrl={restaurant.google_review_url}
                  theme={theme}
                  t={t}
                />
              </div>
            )}
          </>
        )}
        {refunded && (
          <>
            <div className="w-20 h-20 rounded-full bg-amber-100 grid place-items-center mx-auto mb-5">
              <CheckCircle2 className="w-10 h-10 text-amber-600" />
            </div>
            <h1 data-testid="payment-refunded" className="font-serif-display text-2xl text-[#123026]">Paiement remboursé</h1>
            <p className="text-sm text-[#8A918D] mt-2">Ce paiement a été remboursé par le restaurant.</p>
          </>
        )}
        {isError && (
          <>
            <div className="w-20 h-20 rounded-full bg-red-100 grid place-items-center mx-auto mb-5">
              <XCircle className="w-10 h-10 text-[#DC2626]" />
            </div>
            <h1 data-testid="payment-failed" className="font-serif-display text-2xl text-[#123026]">{errorTitle}</h1>
            <p className="text-sm text-[#8A918D] mt-2">{errorHint}</p>
          </>
        )}
        {!checking && (
          <Button
            data-testid="payment-back-btn"
            onClick={() => navigate(backTo)}
            className="w-full h-12 rounded-full text-white font-semibold mt-8"
            style={{ backgroundColor: "#123026" }}
          >
            {isError ? "Réessayer depuis ma table" : "Retour à ma table"}
          </Button>
        )}
        <p className="text-center text-[11px] text-[#B4B9B4] pt-8">Powered by NOVANEXT</p>
      </div>
    </div>
  );
}
